import JSONbig from 'json-bigint';
import isEqual from 'lodash/isEqual';
import { useEffect, useRef } from 'react';

import type { ModelLead } from '@app/api/Leads/types';
import type { RecommendLeadEvent } from '@app/api/RecommendChat/types';
import { useUserPrint } from '@app/hooks';
import { useLatestRegistryResultBridge } from '@app/pages/Leads/model';
import { playLeadsUpdateSound } from '@app/utils';
import { clogLeadsRegistryDebug } from '@app/utils/clog';
import {
  createWSConnection,
  invokeWSMethod,
  subscribeToWsEvent,
  unsubscribeFromWsEvent,
} from '@app/utils/createWSConnection';

import { shouldPlayRecommendUpdateSound } from '../lib/shouldPlayRecommendUpdateSound';

import { useLeadsRegistrySettings } from './useLeadsRegistrySettings';

const LEADS_GROUP_EVENT = 'onLeadsGroupUpdate';
const LISTEN_LEADS_GROUP_METHOD = 'ListenLeadsGroup';
const SOUND_THROTTLE_MS = 1500;
const SOUND_VOLUME = 1;

interface LeadsGroupUpdatePayload {
  SearchId: unknown;
  Items?: ModelLead[] | null;
  RecommendChatEvent?: RecommendLeadEvent | null;
}

interface LeadsGroupUpdateEvent {
  jsonData: string;
}

interface UseLeadsRegistryRealtimeParams {
  isEnabled?: boolean;
}

// Registry realtime ownership for Leads. Subscribes to the accepted
// `SearchId`, applies matching list patches, and tells the server to drop the
// previous accepted group via the `deleteSearchId` argument. Mounted by the
// registry page and by the registry-sourced lead card list; routes are
// exclusive, so only one subscriber owns the event at a time.
export const useLeadsRegistryRealtime = ({
  isEnabled = true,
}: UseLeadsRegistryRealtimeParams = {}) => {
  const { params } = useUserPrint();
  const database = params.Database;
  const { isIncomingMessageSoundEnabled } = useLeadsRegistrySettings();
  const { searchId, leads, patchLeads } = useLatestRegistryResultBridge();

  const listenerIdRef = useRef(0);
  const currentSearchIdRef = useRef<string | null>(null);
  const prevSearchIdRef = useRef<string | null>(null);
  const lastEventRef = useRef<{
    searchId: string | null;
    payload: string | null;
  }>({ searchId: null, payload: null });
  const leadsRef = useRef<ModelLead[]>(leads);
  const isSoundEnabledRef = useRef(isIncomingMessageSoundEnabled);
  const recommendSeenRef = useRef(new Map<string, string>());

  useEffect(() => {
    recommendSeenRef.current.clear();
  }, [database]);

  useEffect(() => {
    leadsRef.current = leads;
  }, [leads]);

  useEffect(() => {
    currentSearchIdRef.current = searchId;
  }, [searchId]);

  useEffect(() => {
    isSoundEnabledRef.current = isIncomingMessageSoundEnabled;
  }, [isIncomingMessageSoundEnabled]);

  useEffect(() => {
    if (!isEnabled || !database || !searchId) return;

    const deleteSearchId =
      prevSearchIdRef.current && prevSearchIdRef.current !== searchId
        ? prevSearchIdRef.current
        : null;

    const listenerId = ++listenerIdRef.current;
    const isLatestListener = () => listenerId === listenerIdRef.current;

    lastEventRef.current = { searchId: null, payload: null };

    const playUpdateSoundIfAllowed = () => {
      const isUserActive = navigator.userActivation?.hasBeenActive === true;

      if (
        isSoundEnabledRef.current &&
        isUserActive &&
        document.visibilityState === 'visible'
      ) {
        try {
          playLeadsUpdateSound(SOUND_THROTTLE_MS, SOUND_VOLUME);
        } catch {
          // Ignore browser autoplay rejection (NotAllowedError).
        }
      }
    };

    const leadsGroupUpdateHandler = (event: LeadsGroupUpdateEvent) => {
      if (!isLatestListener()) return;

      const payload = JSONbig.parse(event.jsonData) as LeadsGroupUpdatePayload;
      const incomingSearchId = String(payload.SearchId);

      // TEMP DEBUG issue/10291 — что прислал сокет: SearchId + id:имя лидов.
      const incomingItems = payload.Items ?? [];
      clogLeadsRegistryDebug(
        database,
        `[10291] WS данные: SearchId=${incomingSearchId}, лидов=${incomingItems.length}`,
        incomingItems
          .map((lead) => `${lead.Id}:${lead.ClientName ?? '—'}`)
          .join(', ')
      );

      const isDuplicatePayload =
        lastEventRef.current.searchId === incomingSearchId &&
        lastEventRef.current.payload === event.jsonData;

      if (isDuplicatePayload) return;
      if (incomingSearchId !== currentSearchIdRef.current) return;

      lastEventRef.current = {
        searchId: incomingSearchId,
        payload: event.jsonData,
      };

      const items = payload.Items ?? [];

      // Single-pass reference-stable merge: preserve prev refs for unchanged
      // leads so the WeakMap row cache and React.memo cells skip re-rendering
      // when only a subset of leads changes in a realtime patch.
      const currentById = new Map(leadsRef.current.map((l) => [l.Id, l]));
      let hasChanges = items.length !== leadsRef.current.length;
      const stableItems = items.map((item) => {
        const existing = currentById.get(item.Id);
        if (existing !== undefined && isEqual(existing, item)) return existing;
        hasChanges = true;
        return item;
      });

      if (!hasChanges) return;

      if (
        shouldPlayRecommendUpdateSound(
          payload.RecommendChatEvent,
          items.map((item) => item.RecommendChatEvent),
          recommendSeenRef.current
        )
      ) {
        playUpdateSoundIfAllowed();
      }
      patchLeads(stableItems);
    };

    const subscribe = async () => {
      await createWSConnection(async () => {
        if (!isLatestListener()) return;

        // TEMP DEBUG issue/10291 — на какую группу подписались и какую сняли.
        clogLeadsRegistryDebug(
          database,
          `[10291] WS подписка: listen=${searchId}, delete=${
            deleteSearchId ?? '—'
          }`
        );

        await invokeWSMethod(
          LISTEN_LEADS_GROUP_METHOD,
          database,
          searchId,
          deleteSearchId
        );

        if (!isLatestListener()) return;

        subscribeToWsEvent(LEADS_GROUP_EVENT, leadsGroupUpdateHandler);
      });
    };

    void subscribe();
    prevSearchIdRef.current = searchId;

    return () => {
      listenerIdRef.current += 1;
      unsubscribeFromWsEvent(LEADS_GROUP_EVENT);
    };
  }, [database, isEnabled, searchId, patchLeads]);
};
